import React from 'react';
import { Clock, BookOpen, Play, CheckCircle, Star, Award } from 'lucide-react';

const ModuleCard = ({ module, onProgressUpdate, onBookmarkToggle, isBookmarked }) => {
  const handleStartModule = () => {
    const progressData = {
      status: module.progress?.status === 'completed' ? 'completed' : 'in_progress',
      progressPercentage: module.progress?.progressPercentage || 0,
      timeSpent: module.progress?.timeSpent || 0
    };
    
    onProgressUpdate(module.id, progressData);
  };

  const getStatusBadge = () => {
    if (!module.progress) {
      return (
        <span className="bg-gray-100 text-gray-600 px-2 py-1 text-xs font-medium rounded-full">
          Not Started
        </span>
      );
    }

    switch (module.progress?.status) {
      case 'completed':
        return (
          <span className="bg-green-100 text-green-800 px-2 py-1 text-xs font-medium rounded-full flex items-center">
            <CheckCircle className="h-3 w-3 mr-1" />
            Completed
          </span>
        );
      case 'in_progress':
        return (
          <span className="bg-blue-100 text-blue-800 px-2 py-1 text-xs font-medium rounded-full">
            In Progress
          </span>
        );
      default:
        return (
          <span className="bg-gray-100 text-gray-600 px-2 py-1 text-xs font-medium rounded-full">
            Not Started
          </span>
        );
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner':
        return 'bg-green-100 text-green-800';
      case 'intermediate':
        return 'bg-yellow-100 text-yellow-800';
      case 'advanced':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg hover:shadow-lg transition-all duration-200 overflow-hidden">
      {/* Header */}
      <div className="p-6 pb-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-2">
            <span className="bg-green-100 text-green-800 px-2 py-1 text-xs font-medium rounded-full">
              Chapter {module.chapterNumber}
            </span>
            
            {module.isPremium && (
              <span className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-2 py-1 text-xs font-medium rounded-full flex items-center">
                <Star className="h-3 w-3 mr-1" />
                Premium
              </span>
            )}
          </div>
          
          <button
            onClick={() => onBookmarkToggle(module.id)}
            className={`p-1 rounded-md transition-colors ${
              isBookmarked 
                ? 'text-yellow-600 hover:text-yellow-700' :'text-gray-400 hover:text-gray-600'
            }`}
            title={isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
          >
            <BookOpen className="h-4 w-4" />
          </button>
        </div>

        <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
          {module.title}
        </h3>
        
        <p className="text-gray-600 text-sm mb-4 line-clamp-3">
          {module.description}
        </p>

        {/* Badges */}
        <div className="flex items-center space-x-2 mb-4">
          {getStatusBadge()}
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getDifficultyColor(module.difficulty)}`}>
            {module.difficulty?.charAt(0)?.toUpperCase() + module.difficulty?.slice(1)}
          </span>
        </div>

        {/* Progress Bar */}
        {module.progress && module.progress?.progressPercentage > 0 && (
          <div className="mb-4">
            <div className="flex justify-between text-sm text-gray-600 mb-1">
              <span>Progress</span>
              <span>{module.progress?.progressPercentage}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all ${
                  module.progress?.status === 'completed' ? 'bg-green-600' : 'bg-blue-500'
                }`}
                style={{ width: `${module.progress?.progressPercentage}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>
      {/* Content Preview */}
      {module.content?.sections && (
        <div className="px-6 pb-4">
          <h4 className="text-sm font-medium text-gray-900 mb-2">What You'll Learn:</h4>
          <ul className="text-sm text-gray-600 space-y-1">
            {module.content?.sections?.slice(0, 3)?.map((section, index) => (
              <li key={index} className="flex items-start">
                <span className="text-green-600 mr-2">•</span>
                <span className="line-clamp-1">{section?.title}</span>
              </li>
            ))}
            {module.content?.sections?.length > 3 && (
              <li className="text-gray-500 text-xs">
                +{module.content?.sections?.length - 3} more sections
              </li>
            )}
          </ul>
        </div>
      )}
      {/* Footer */}
      <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 text-sm text-gray-500">
            <div className="flex items-center">
              <Clock className="h-4 w-4 mr-1" />
              {module.estimatedDuration} min
            </div>
            
            {module.progress?.timeSpent > 0 && (
              <div className="flex items-center">
                <Award className="h-4 w-4 mr-1" />
                {module.progress?.timeSpent}m spent
              </div>
            )}
          </div>

          <button
            onClick={handleStartModule}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center text-sm font-medium"
          >
            <Play className="h-4 w-4 mr-2" />
            {module.progress?.status === 'completed' ? 'Review' : 
             module.progress?.status === 'in_progress' ? 'Continue' : 'Start'}
          </button>
        </div>

        {/* Learning Objectives Preview */}
        {module.learningObjectives && module.learningObjectives?.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="text-xs text-gray-500 mb-1">Key Objectives:</div>
            <div className="flex flex-wrap gap-1">
              {module.learningObjectives?.slice(0, 2)?.map((objective, index) => (
                <span key={index} className="bg-green-50 text-green-700 px-2 py-1 text-xs rounded-md">
                  {objective?.length > 30 ? objective?.substring(0, 30) + '...' : objective}
                </span>
              ))}
              {module.learningObjectives?.length > 2 && (
                <span className="bg-gray-100 text-gray-600 px-2 py-1 text-xs rounded-md">
                  +{module.learningObjectives?.length - 2} more
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModuleCard;